import React, { useEffect, useState } from 'react'

import {
  BoardGroupFragment,
  CreatePlanItemInput,
  useAddPlanItemMutation,
  useMarkAllCommentsAsReadMutation,
  BoardGroupFragmentDoc,
  TermOption,
  BoardAlertFragment,
  PlanningTerm,
  BoardItemFragment,
  useItemFromPlaceholderTemplateMutation,
  PlanItem,
  SchedulerActiveTerm
} from '../../../../generated/graphql'
import PlanGroupLane from './PlanGroupLane'
import { CompletedCoursesData, ModalName } from '../../../../types'
import { updateAddPlanItemCache } from '../../../../lib/cacheUtils'
import useMixpanelEvent from '../../../../lib/hooks/useMixpanelEvent'
import useModal from '../../../../lib/hooks/useModal'
import useNotification from '../../../../lib/hooks/useNotification'
import useSchedulerLogin from '../../../../lib/hooks/useSchedulerLogin'

export type PlanGroupLaneWrapperProps = {
  /** List of alerts belonging to the plan */
  alerts: Array<BoardAlertFragment>
  /** Terms that are currently active */
  activeTerms?: SchedulerActiveTerm[]
  /** Campus used to filter courses */
  campus?: string
  /** Current Plan Group */
  group: BoardGroupFragment
  /** Indicates if this is the user's primary plan */
  isPrimaryPlan?: boolean | undefined
  /** Indicates if this plan is a template */
  isTemplate: boolean
  /** The ownerAppUserId of the plan owner */
  ownerAppUserId: number
  /** Indicates if this plan/template is read-only */
  readOnly?: boolean
  /** Optional list of configured term options to use for template term naming */
  termOptions?: Array<TermOption>
  /** List of terms to display in the term select on a plan */
  terms?: Array<PlanningTerm>
}

const defaultProps: Pick<
  PlanGroupLaneWrapperProps,
  'activeTerms' | 'campus' | 'isPrimaryPlan' | 'readOnly' | 'termOptions' | 'terms'
> = {
  activeTerms: undefined,
  campus: undefined,
  isPrimaryPlan: false,
  readOnly: false,
  termOptions: undefined,
  terms: undefined
}

const PlanGroupLaneWrapper = ({
  alerts,
  activeTerms,
  campus,
  group,
  isPrimaryPlan,
  isTemplate,
  ownerAppUserId,
  readOnly,
  termOptions,
  terms
}: PlanGroupLaneWrapperProps) => {
  const { externalTermId, id, items, planId, title } = group
  const [isCourseOnly, setIsCourseOnly] = useState(false)
  const [showAddComment, setShowAddComment] = useState(false)
  const [showComments, setShowComments] = useState(false)
  const [showInput, setShowInput] = useState(false)
  const [showNewCommentIndicator, setShowNewCommentIndicator] = useState(true)
  const [showPlaceholderInput, setShowPlaceholderInput] = useState(false)
  const { showErrorNotif } = useNotification()
  const { showModal } = useModal()
  const [commentsRead] = useMarkAllCommentsAsReadMutation({
    onError: () => {
      showErrorNotif('Could not mark comments as read.')
    }
  })
  const { addMixpanelEvent } = useMixpanelEvent()
  const { doSchedulerLogin } = useSchedulerLogin(ownerAppUserId, group.externalTermId || undefined)
  const handleScheduleClick = () => {
    if (
      items.some((item) => item.usedToMeetRequirement || item.inProgress || item.courseTakenInReq)
    ) {
      const completedCoursesData: CompletedCoursesData = {
        completedItems: items.filter(
          (item) => item.usedToMeetRequirement || item.inProgress || item.courseTakenInReq
        ),
        groupTitle: title,
        planId,
        termCode: externalTermId || ''
      }
      showModal(ModalName.ScheduleCompletedCourses, {
        completedCoursesData: [completedCoursesData],
        ownerAppUserId
      })
    } else {
      doSchedulerLogin()
    }
  }

  const [addPlanItemMutation] = useAddPlanItemMutation({
    onCompleted: () => {
      setShowInput(false)
      setIsCourseOnly(false)
      setShowPlaceholderInput(false)
    },
    onError: () => {
      showErrorNotif('Could not add item.', addPlanItemMutation)
    },
    update: (cache, { data }) =>
      updateAddPlanItemCache(cache, group, data?.addPlanItem as PlanItem, ownerAppUserId)
  })

  const [addPlaceholderFromTemplateMutation] = useItemFromPlaceholderTemplateMutation({
    onCompleted: () => {
      setShowPlaceholderInput(false)
    },
    onError: () => {
      showErrorNotif('Could not add item.')
    },
    update: (cache, { data }) =>
      updateAddPlanItemCache(
        cache,
        group,
        data?.itemFromPlaceholderTemplate as PlanItem,
        ownerAppUserId
      )
  })

  const newCommentCount = group.comments.filter((cmt) => !cmt.read).length

  useEffect(() => {
    if (group.comments.find((c) => !c.read)) {
      setShowNewCommentIndicator(true)
    }
  }, [group.comments])

  const isActiveTerm = activeTerms
    ? !!activeTerms.find((t) => t?.termCode === externalTermId)
    : false
  const groupAlerts = alerts.filter((a) => a.groupId === group.id)

  // TODO: FUTURE to do optimistic responses, we need to be able to inject the id at mutation time
  // or use a different key to avoid double re-renders.
  const onAddPlanItem = (planItemState: CreatePlanItemInput) => {
    addPlanItemMutation({
      variables: {
        input: {
          courseNumber: planItemState.courseNumber,
          creditsMin: planItemState.creditsMin,
          creditsMax: planItemState.creditsMax,
          description: planItemState.description,
          externalCourseId: planItemState.externalCourseId,
          groupId: id,
          nonCourseCreditsMin: planItemState.nonCourseCreditsMin,
          nonCourseCreditsMax: planItemState.nonCourseCreditsMax,
          nonCourseTitle: planItemState.nonCourseTitle,
          planId,
          subjectId: planItemState.subjectId,
          title: planItemState.title
        }
      }
    })
  }

  const onAddPlaceholderItem = (placeholder: BoardItemFragment) => {
    const { id: placeholderId, nonCourseTitle } = placeholder

    // If id is blank, adding a new placeholder item not from a template
    if (placeholderId === '') {
      addPlanItemMutation({
        variables: {
          input: {
            groupId: id,
            nonCourseTitle,
            planId,
            title: nonCourseTitle
          }
        }
      })
      if (!isTemplate) {
        addMixpanelEvent('Student Profile > Plan: Add Non-course to Plan')
      }
    } else {
      // Otherwise adding an item from a placeholder template
      addPlaceholderFromTemplateMutation({
        variables: {
          input: {
            groupId: id,
            placeholderTemplateId: placeholderId,
            planId
          }
        }
      })
      if (!isTemplate) {
        addMixpanelEvent('Student Profile > Plan: Add Placeholder to Plan')
      }
    }
  }

  const onDeleteGroup = () => {
    showModal(ModalName.ConfirmDeleteGroup, { id, isTemplate, planId })
  }

  // When adding a course (and not a generic item), the only difference
  // is we're changing the label on the autosuggest and not showing the
  // static add option.  Either option will use the same component, but
  // with a few modifications if we're only looking up courses.
  const onShowCourseInput = () => {
    setIsCourseOnly(true)
    setShowInput(true)
  }

  const onShowInput = () => {
    setShowInput(true)
  }

  const onShowPlaceholderInput = () => {
    setShowPlaceholderInput(true)
  }

  const onCancelAddItem = () => {
    setIsCourseOnly(false)
    setShowInput(false)
    setShowPlaceholderInput(false)
  }

  const onMarkCommentsRead = () => {
    commentsRead({
      variables: {
        input: id // The groupId, not comment
      },
      update: (proxy) => {
        // Read the data from our cache for our plan fragment
        const cacheId = `PlanGroup:${id}`
        const cacheGroup = proxy.readFragment<BoardGroupFragment>({
          id: cacheId,
          fragment: BoardGroupFragmentDoc,
          fragmentName: 'boardGroup'
        })

        if (cacheGroup) {
          // Mark all comments as read
          const newCacheGroup = { ...cacheGroup }
          const newComments = newCacheGroup.comments.map((c) => {
            const newComment = { ...c }
            newComment.read = true
            return newComment
          })
          newCacheGroup.comments = newComments

          // Write data back to the cache, with updated comments
          proxy.writeFragment({
            id: cacheId,
            fragment: BoardGroupFragmentDoc,
            data: newCacheGroup,
            fragmentName: 'boardGroup'
          })
        }
      }
    })
  }

  const onMarkCommentsReadMobile = () => {
    setShowNewCommentIndicator(false)
    onMarkCommentsRead()
  }

  const onAddComment = () => {
    setShowAddComment(true)
  }

  const onCancelAddComment = () => {
    setShowAddComment(false)
    setShowComments(false)
    setShowNewCommentIndicator(false)
    onMarkCommentsRead()
  }

  const onCountClick = () => {
    setShowComments(true)
  }

  const onSaveComment = () => {
    if (group.comments.length === 1) {
      setShowAddComment(false)
      setShowComments(false)
      setShowNewCommentIndicator(false)
      onMarkCommentsRead()
    }
    addMixpanelEvent('Student Profile > Plan: Add Comment to Term')
  }

  return (
    <PlanGroupLane
      alerts={groupAlerts}
      campus={campus}
      group={group}
      isActiveTerm={isActiveTerm}
      isCourseOnly={isCourseOnly}
      isPrimaryPlan={isPrimaryPlan}
      isTemplate={isTemplate}
      newCommentCount={newCommentCount}
      onAddComment={onAddComment}
      onAddPlaceholderItem={onAddPlaceholderItem}
      onAddPlanItem={onAddPlanItem}
      onCancelAddComment={onCancelAddComment}
      onCancelAddItem={onCancelAddItem}
      onCountClick={onCountClick}
      onDeleteGroup={onDeleteGroup}
      onMarkCommentsRead={onMarkCommentsReadMobile}
      onSaveComment={onSaveComment}
      onScheduleClick={handleScheduleClick}
      onShowCourseInput={onShowCourseInput}
      onShowInput={onShowInput}
      onShowPlaceholderInput={onShowPlaceholderInput}
      ownerAppUserId={ownerAppUserId}
      readOnly={readOnly}
      showAddComment={showAddComment}
      showComments={showComments}
      showInput={showInput}
      showNewCommentIndicator={showNewCommentIndicator}
      showPlaceholderInput={showPlaceholderInput}
      termOptions={termOptions}
      terms={terms}
      totalCommentCount={group?.comments?.length}
    />
  )
}

PlanGroupLaneWrapper.defaultProps = defaultProps

export default PlanGroupLaneWrapper
